// @ts-strict-ignore
import type { Developer } from "@dashboardApi/apis/developer";

import { getPermission, getPermissionScope } from "@lib/permissionConstants";

import type { DeveloperPermissionInfo, DeveloperPermissionMap } from "./types";

/**
 * Given an array of permissions, create a map that allows O(1) lookup of permissions by name and scope
 *
 * The supported scopes are:
 *
 * - company
 * - appGroup
 * - team
 * - appGroupOrAnyTeamInAppGroup (pseudo-scope)
 *
 * The map also supports multi-company users by including the company ID in the key for app groups
 *
 * This allows the implementation of `useDeveloperHasPermission` to avoid an extra backend API call
 * for the special appGroupOrAnyTeamInAppGroup pseudo-scope. For this scope, the user will be marked
 * as having permission if the user has app group-level permission OR team-level permission for any
 * team in the app group. This is useful for permissions like `basicReadOnly` that should be granted
 * for viewing a page that can be viewed at the app group or team-specific level.
 * @param permissions
 * @returns
 */
export const createPermissionsMap = (permissions: Developer["permissions"]): DeveloperPermissionMap => {
  const permissionsMap: DeveloperPermissionMap = new Map();
  permissions.forEach((permission) => {
    const {
      name: encodedPermissionName,
      scope,
      context: { app_group_id: appGroupId, company_id: companyId },
      scope_id: scopeId,
    } = permission;
    const permissionName = getPermission(encodedPermissionName);
    const permissionInfo: DeveloperPermissionInfo = permissionsMap.get(permissionName) || {
      company: new Set(),
      appGroups: new Set(),
      anyTeam: new Set(),
      teams: new Map(),
    };
    switch (getPermissionScope(scope)) {
      case "company":
        permissionInfo.company.add(scopeId);
        break;
      case "appGroup":
        permissionInfo.appGroups.add(`${companyId}-${scopeId}`);
        break;
      case "team": {
        // mark this permission/app group combination as containing teams that the user has the team-level permission in order to
        // support the `appGroupOrAnyTeamInAppGroup` pseudo-scope
        permissionInfo.anyTeam.add(`${companyId}-${appGroupId}`);
        const teamsSet: Set<string> = permissionInfo.teams.get(`${companyId}-${appGroupId}`) || new Set();
        teamsSet.add(scopeId);
        // also mark the team-level permission for the user as being granted
        permissionInfo.teams.set(`${companyId}-${appGroupId}`, teamsSet);
        break;
      }
    }
    permissionsMap.set(permissionName, permissionInfo);
  });

  return permissionsMap;
};
